// ── 홍보물 주문 모바일 카드 ────────────────────────────────────────────────────
import React from 'react';
import { WorkflowStepBadge, OrderedCheckbox, CompletedCheckbox } from './OrderItemComponents';
import { resolvePromoRowState } from './promoRowState';
import { useIsDark } from '../../../hooks/useDarkMode';
import { orderCardStyle, editBtnStyle, deleteBtnStyle, resolvePromoRowColors } from './styles';
import { PromoCompanyAssign } from './PromoCompanyAssign';

export function OrderCard({ order, onEdit, onDelete, onItemStepChange, onItemStepToggle }) {
  const isDark = useIsDark();
  // 행 상태(색/필터 단일 진실) — promoRowState.resolvePromoRowState
  const state = resolvePromoRowState(order);
  // 상태별 배경색 — isDark에 따라 불투명도 조정 (계약 관리와 동일 패턴)
  const c = resolvePromoRowColors(state === 'shipped', state === 'in_progress', isDark);
  return (
    // 왼쪽 4px 색 막대는 뺐다(2026-10-23, PC 표와 같이 — 사용자 선택) — 상태는 카드 배경색으로 본다
    <div style={{ ...orderCardStyle, background: c.bg, border: `1px solid ${c.border}` }}>
      {/* 상단: 상호 + 날짜/카테고리/담당자 */}
      <div style={{ marginBottom: 6 }}>
        <div style={{ fontWeight: 800, fontSize: 14 }}>{order.company_name || '-'}</div>
        <PromoCompanyAssign order={order} />
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
          {order.received_date}
          {order.category_name && <span style={{ marginLeft: 6, background: 'var(--bg-subtle)', color: '#7c3aed', borderRadius: 4, padding: '1px 5px', fontSize: 10, fontWeight: 700 }}>{order.category_name}</span>}
          {order.sales_person_name ? ` · ${order.sales_person_name}` : ''}
        </div>
      </div>
      {order.address && (
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>{order.address}</div>
      )}
      {order.memo && (
        <div style={{ fontSize: 11, color: 'var(--text-sub)', background: 'rgba(234,179,8,0.1)', borderRadius: 4, padding: '2px 6px', marginBottom: 6, display: 'inline-block', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>📝 {order.memo}</div>
      )}
      {/* 아이템 목록 — 진행상태 배지 + 이름/수량 + 택배 */}
      {order.items?.length > 0 && (
        <div style={{ marginBottom: 8, display: 'flex', flexDirection: 'column', gap: 5 }}>
          {order.items.map((i, k) => (
            <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', opacity: i.is_cancelled ? 0.5 : 1 }}>
              {/* 아이템별 진행상태 배지/체크박스
                  - 취소된 아이템: 표시 안 함
                  - 배송불필요+custom_steps: WorkflowStepBadge (마지막이면 완료 배지)
                  - 배송불필요+custom_steps 없음: 완료 체크박스 (is_ordered 기반)
                  - 일반+택배정보 완비: 완료 배지
                  - 일반+택배정보 없음: 발주 체크박스 */}
              {i.is_cancelled
                ? null  // 취소된 아이템은 체크박스/배지 숨김
                : i.no_shipping && Array.isArray(i.custom_steps) && i.custom_steps.length > 0
                  ? (i.workflow_step_id === i.custom_steps.length
                      ? <span style={{ fontSize: 11, fontWeight: 700, color: '#15803d', background: 'rgba(34,197,94,0.15)', borderRadius: 6, padding: '2px 8px', border: '1px solid rgba(34,197,94,0.4)', flexShrink: 0 }}>완료</span>
                      : <WorkflowStepBadge
                          stepId={i.workflow_step_id}
                          steps={i.custom_steps.map((s, si) => ({ id: si + 1, name: s.name, sort_order: si, is_active: true }))}
                          onStepChange={(newId) => onItemStepChange && onItemStepChange(order.id, i.id, newId)}
                        />
                    )
                  : i.no_shipping
                    ? <CompletedCheckbox
                        isCompleted={i.is_ordered}
                        completedBy={i.ordered_by}
                        onToggle={() => onItemStepToggle && onItemStepToggle(order.id, i.id, i.is_ordered)}
                      />
                    : (i.courier?.trim() && i.tracking_number?.trim())
                      ? <span style={{ fontSize: 11, fontWeight: 700, color: '#15803d', background: 'rgba(34,197,94,0.15)', borderRadius: 6, padding: '2px 8px', border: '1px solid rgba(34,197,94,0.4)', flexShrink: 0 }}>완료</span>
                      : <OrderedCheckbox
                          isOrdered={i.is_ordered}
                          orderedBy={i.ordered_by}
                          onToggle={() => onItemStepToggle && onItemStepToggle(order.id, i.id, i.is_ordered)}
                        />
              }
              {/* 홍보물 이름 + 수량 — 취소 시 취소선 */}
              <span style={{ fontSize: 12, color: i.is_cancelled ? 'var(--text-muted)' : 'var(--text-sub)', textDecoration: i.is_cancelled ? 'line-through' : 'none' }}>{i.item_name} {i.quantity}개</span>
              {/* 택배정보 — 송장불필요 항목은 뱃지, 그 외 택배정보 있으면 표시 */}
              {i.no_shipping ? (
                <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 10, background: 'var(--border)', color: 'var(--text-muted)' }}>
                  송장불필요
                </span>
              ) : (i.courier || i.tracking_number) && (
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                  {[i.courier, i.tracking_number].filter(Boolean).join(' · ')}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
      {/* 버튼 — 수정 + 삭제 */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 6 }}>
        <button style={editBtnStyle} onClick={() => onEdit(order)}>수정</button>
        <button style={deleteBtnStyle} onClick={() => onDelete(order)}>삭제</button>
      </div>
    </div>
  );
}
